import React from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Truck,
  Calendar,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { Badge } from './shared/Badge';

interface CarrierSwitch {
  fromDotNumber: number;
  fromCarrierName: string;
  lastSeenAt: string;
  toDotNumber: number;
  toCarrierName: string;
  firstSeenAt: string;
  daysBetween: number;
  suspicious: boolean;
  suspicionReasons: string[];
}

interface CarrierPeriod {
  dotNumber: number;
  carrierName: string;
  startDate: string;
  endDate: string;
  eventCount: number;
}

interface TimelineEvent {
  vin: string;
  dotNumber: number;
  carrierName: string;
  eventType: 'crash' | 'inspection';
  eventDate: string;
  hadViolations?: boolean;
  hadOOS?: boolean;
  hadCrash?: boolean;
  hadFatalities?: boolean;
  hadInjuries?: boolean;
  location?: string;
  inspectionLevel?: string;
  inspectionId?: string | number;  // Add inspection ID for linking
  reportNumber?: string;
}

interface CarrierSwitchTimelineProps {
  switches: CarrierSwitch[];
  carrierPeriods: CarrierPeriod[];
  timeline?: TimelineEvent[];
  compact?: boolean;
  onInspectionClick?: (inspectionId: string | number, dotNumber: number) => void;
}

// Helper function to format dates
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Helper to determine event severity
const getEventSeverity = (event: TimelineEvent): 'clean' | 'warning' | 'danger' => {
  if (event.eventType === 'crash' || event.hadFatalities || event.hadInjuries) {
    return 'danger';
  }
  if (event.hadOOS || event.hadViolations) {
    return 'warning';
  }
  return 'clean';
};

// Event dot component
const EventDot: React.FC<{ event: TimelineEvent; showDetails?: boolean }> = ({ event, showDetails }) => {
  const severity = getEventSeverity(event);

  const colorClasses = {
    clean: 'bg-green-500',
    warning: 'bg-yellow-500',
    danger: 'bg-red-500',
  };

  return (
    <div className="relative group">
      <div className={`w-3 h-3 rounded-full ${colorClasses[severity]} ring-2 ring-white cursor-pointer`} />
      {/* Tooltip on hover */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
        <div className="bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
          {event.eventType === 'crash' ? 'Crash' : 'Inspection'} - {formatDate(event.eventDate)}
          {event.hadOOS && ' (OOS)'}
          {event.hadViolations && !event.hadOOS && ' (Violations)'}
        </div>
      </div>
    </div>
  );
};

// Switch indicator component
const SwitchIndicator: React.FC<{ sw: CarrierSwitch; index: number }> = ({ sw, index }) => {
  return (
    <div className={`flex flex-col items-center px-2 py-1 ${sw.suspicious ? 'bg-red-50' : 'bg-gray-50'} rounded-lg border ${sw.suspicious ? 'border-red-300' : 'border-gray-200'} mx-1`}>
      <ArrowRight className={`w-4 h-4 ${sw.suspicious ? 'text-red-500' : 'text-gray-400'}`} />
      <span className={`text-xs ${sw.suspicious ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
        {sw.daysBetween}d
      </span>
      {sw.suspicious && (
        <AlertTriangle className="w-3 h-3 text-red-500 mt-1" />
      )}
    </div>
  );
};

// Carrier block component for timeline
const CarrierBlock: React.FC<{
  period: CarrierPeriod;
  events: TimelineEvent[];
  isFirst: boolean;
  isLast: boolean;
  onInspectionClick?: (inspectionId: string | number, dotNumber: number) => void;
}> = ({ period, events, isFirst, isLast, onInspectionClick }) => {
  const periodEvents = events.filter(
    e => e.dotNumber === period.dotNumber &&
    new Date(e.eventDate) >= new Date(period.startDate) &&
    new Date(e.eventDate) <= new Date(period.endDate)
  );

  const badEventCount = periodEvents.filter(e =>
    e.eventType === 'crash' || e.hadOOS || e.hadViolations
  ).length;

  const cleanEventCount = periodEvents.filter(e =>
    e.eventType === 'inspection' && !e.hadOOS && !e.hadViolations
  ).length;

  const hasMostlyBadEvents = badEventCount > 0 && badEventCount >= cleanEventCount;

  return (
    <div className={`flex-shrink-0 p-3 rounded-lg border-2 ${
      hasMostlyBadEvents ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
    } min-w-[220px] max-w-[280px]`}>
      <div className="flex items-center gap-2 mb-2">
        <Truck className={`w-4 h-4 ${hasMostlyBadEvents ? 'text-red-500' : 'text-blue-500'}`} />
        <span className="font-semibold text-sm text-gray-900 truncate" title={period.carrierName}>
          {period.carrierName}
        </span>
      </div>
      <div className="text-xs text-gray-600">
        DOT {period.dotNumber}
      </div>
      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
        <Calendar className="w-3 h-3" />
        <span>{formatDate(period.startDate)}</span>
      </div>

      {/* Detailed event list */}
      {periodEvents.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
          {periodEvents.slice(0, 5).map((event, idx) => (
            <div key={idx} className="text-xs">
              <div className="font-medium text-gray-700">{formatDate(event.eventDate)}</div>
              <div className="flex items-center gap-1 mt-0.5">
                {event.inspectionLevel && (
                  <span className="text-gray-500">Level {event.inspectionLevel}</span>
                )}
                {event.eventType === 'inspection' ? (
                  <>
                    {event.hadOOS || event.hadViolations ? (
                      <span className="text-red-600 font-semibold">VIOLATIONS</span>
                    ) : (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="w-3 h-3" />
                        CLEAN INSPECTION
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-red-600 font-semibold">CRASH</span>
                )}
              </div>
              {event.location && (
                <div className="text-gray-500 truncate" title={event.location}>
                  {event.location}
                </div>
              )}
              {event.inspectionId && onInspectionClick && (
                <button
                  onClick={() => onInspectionClick(event.inspectionId!, period.dotNumber)}
                  className="text-blue-600 hover:underline text-xs mt-0.5"
                >
                  Inspection #{event.inspectionId}
                </button>
              )}
            </div>
          ))}
          {periodEvents.length > 5 && (
            <div className="text-xs text-gray-500 italic">
              +{periodEvents.length - 5} more events
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const CarrierSwitchTimeline: React.FC<CarrierSwitchTimelineProps> = ({
  switches,
  carrierPeriods,
  timeline = [],
  compact = false,
  onInspectionClick
}) => {
  if (carrierPeriods.length === 0) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
        No carrier history available
      </div>
    );
  }

  // Compact view for dashboards
  if (compact) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Truck className="w-5 h-5 text-blue-600" />
          Carrier Timeline ({carrierPeriods.length} carriers)
        </h4>
        <div className="space-y-2">
          {carrierPeriods.map((period, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${
                idx === carrierPeriods.length - 1 ? 'bg-blue-500' : 'bg-gray-400'
              }`} />
              <span className="text-sm text-gray-900 truncate flex-1" title={period.carrierName}>
                {period.carrierName}
              </span>
              <span className="text-xs text-gray-500">
                {formatDate(period.startDate)}
              </span>
              {idx < switches.length && switches[idx].suspicious && (
                <AlertTriangle className="w-3 h-3 text-red-500" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Calculate suspicious switch indices for highlighting
  const suspiciousSwitchCount = switches.filter(s => s.suspicious).length;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-gray-900 flex items-center gap-2">
            <Truck className="w-5 h-5 text-blue-600" />
            Carrier Switch Timeline
          </h4>
          <div className="flex items-center gap-2">
            <Badge variant="info" size="sm">{carrierPeriods.length} carriers</Badge>
            <Badge variant="warning" size="sm">{switches.length} switches</Badge>
            {suspiciousSwitchCount > 0 && (
              <Badge variant="danger" size="sm">{suspiciousSwitchCount} suspicious</Badge>
            )}
          </div>
        </div>
      </div>

      {/* Timeline visualization */}
      <div className="p-4 overflow-x-auto">
        <div className="flex items-center min-w-max">
          {carrierPeriods.map((period, idx) => (
            <React.Fragment key={idx}>
              <CarrierBlock
                period={period}
                events={timeline}
                isFirst={idx === 0}
                isLast={idx === carrierPeriods.length - 1}
                onInspectionClick={onInspectionClick}
              />
              {idx < switches.length && (
                <SwitchIndicator sw={switches[idx]} index={idx} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center gap-4 text-xs text-gray-600">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span>Clean inspection</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span>Violations</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span>Crash/OOS</span>
          </div>
          <div className="flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 text-red-500" />
            <span>Suspicious switch</span>
          </div>
        </div>
      </div>

      {/* Suspicious switch details */}
      {suspiciousSwitchCount > 0 && (
        <div className="px-4 py-3 border-t border-red-200 bg-red-50">
          <h5 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Suspicious Switches Detected
          </h5>
          <div className="space-y-2">
            {switches.filter(s => s.suspicious).map((sw, idx) => (
              <div key={idx} className="text-sm text-red-700 p-2 bg-white rounded border border-red-200">
                <div className="font-medium">
                  {sw.fromCarrierName} (DOT {sw.fromDotNumber})
                  <ArrowRight className="w-4 h-4 inline mx-2" />
                  {sw.toCarrierName} (DOT {sw.toDotNumber})
                </div>
                <div className="text-xs text-red-600 mt-1">
                  {sw.daysBetween} days between carriers
                </div>
                <ul className="mt-1 text-xs text-red-600 list-disc list-inside">
                  {sw.suspicionReasons.map((reason, rIdx) => (
                    <li key={rIdx}>{reason}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CarrierSwitchTimeline;
